import {
  Repeat,
  CalendarDays,
  CalendarClock,
} from 'lucide-react'
import { useQuery } from 'convex/react'
import { api } from '../../../../convex/_generated/api'
import { useAuth } from '@/contexts/auth-context'
import { cn } from '@/lib/utils'

type CadenceKind = 'interval' | 'daily' | 'monthly'

const CADENCE_STYLE: Record<CadenceKind, { icon: typeof Repeat; tone: string }> = {
  interval: { icon: Repeat, tone: 'bg-blue-50 text-blue-700 border-blue-200' },
  daily: { icon: CalendarClock, tone: 'bg-violet-50 text-violet-700 border-violet-200' },
  monthly: { icon: CalendarDays, tone: 'bg-amber-50 text-amber-700 border-amber-200' },
}

export default function AdminCronjobsPage() {
  const { user, isAuthenticated } = useAuth()
  const isAdmin = isAuthenticated && user?.isAdmin
  const crons = useQuery(api.crons.list)

  if (!isAdmin) {
    return <div className="p-6 text-sm text-neutral-500">Admin access required</div>
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-lg font-semibold text-neutral-900">Cronjobs</h1>
        <p className="text-sm text-neutral-500 mt-0.5">
          Live registry from <span className="font-mono text-neutral-700">convex/crons.ts</span>.
        </p>
      </div>

      <section>
        <div className="rounded-lg border border-neutral-200 bg-white divide-y divide-neutral-100">
          {crons === undefined ? (
            <div className="px-4 py-3 text-sm text-neutral-400">Loading…</div>
          ) : crons.length === 0 ? (
            <div className="px-4 py-3 text-sm text-neutral-400">No crons registered.</div>
          ) : (
            crons.map((c) => {
              const kind = c.cadence.kind as CadenceKind
              const { icon: Icon, tone } = CADENCE_STYLE[kind]
              return (
                <div key={c.name} className="px-4 py-3 flex items-start gap-3">
                  <div className="mt-0.5">
                    <span
                      className={cn(
                        'inline-flex items-center gap-1 px-1.5 py-0.5 rounded border text-[10px] font-medium uppercase tracking-wider',
                        tone,
                      )}
                    >
                      <Icon className="h-3 w-3" strokeWidth={2} />
                      {kind}
                    </span>
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-medium text-neutral-900 truncate">{c.name}</span>
                      <span className="text-[11px] text-neutral-400">·</span>
                      <span className="text-[11px] text-neutral-500">{c.cadenceLabel}</span>
                    </div>
                    <div className="text-[12px] text-neutral-600 mt-0.5">{c.description}</div>
                    <div className="text-[11px] font-mono text-neutral-400 mt-1">{c.target}</div>
                  </div>
                </div>
              )
            })
          )}
        </div>
      </section>
    </div>
  )
}
